// React hooks
import { memo, useCallback, useEffect, useState } from "react";

// Helpers i API servisi
import { validateYup } from "services/forms/validation/validation";
import {
  useHandleDropdownChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";
import {
  createFleetDocumentAPI,
  getFleetDocumentAPI,
  getFleetDocumentTypesAPI,
  updateFleetDocumentAPI,
} from "services/api/fleet/vehicles/documents/api";

// Tipovi i konstante
import { Errors } from "services/forms/validation/types";
import {
  FleetDocumentEditFormValues,
  FleetDocumentType,
  FleetDocumentUpload,
} from "services/api/fleet/vehicles/documents/types";
import { initialFleetVehicleDocumentEditForm } from "services/api/fleet/vehicles/documents/consts";
import { VehicleDocumentEditProps } from "./types";
import { FLEET_VEHICLE_DOCUMENT_EDIT_FORM_SCHEMA } from "./validation";

// UI komponente
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { Loader } from "ui/components/Loader";
import FileUploadControl from "ui/components/FileUpload/FileUploadControl/FileUploadControl";
import { useTranslate } from "helpers";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { TextArea } from "ui/components/TextField/TextArea";
import { transformFleetDocumentEditFormValues } from "services/api/fleet/vehicles/documents/transformations";
import { useDialog } from "ui/modules/public/components/DialogProvider/DialogProvider";

const VehicleDocumentEditCard: React.FC<VehicleDocumentEditProps> = (props) => {
  const { vehicleId, documentId, onSuccess, onClose } = props;

  const { t } = useTranslate();
  const { createDialog } = useDialog();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [documentTypes, setDocumentTypes] = useState<
    FleetDocumentType[]
  >([]);

  const [formValues, setFormValues] =
    useState<FleetDocumentEditFormValues>(
      initialFleetVehicleDocumentEditForm
    );

  const [uploads, setUploads] = useState<FleetDocumentUpload[]>([]);

  useEffect(() => {
    fetchVehicleDocumentTypes();
    setFormValues(initialFleetVehicleDocumentEditForm);
    setUploads([]);

    if (documentId) {
      fetchDocument(documentId);
    }
  }, [documentId]);

  const fetchDocument = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialFleetVehicleDocumentEditForm);
      setUploads([]);
      return;
    }
    setIsLoading(true);

    let result;
    try {
      result = await getFleetDocumentAPI(id);
      if (result) {
        setFormValues(transformFleetDocumentEditFormValues(result));

        if (result.uploads && Array.isArray(result.uploads)) {
          setUploads(result.uploads.map((u: any) => ({
            id: u.id,
            originalFileName: u.originalFileName,
            fileExtension: u.fileExtension,
            fileSizeKb: u.fileSizeKb,
            contentType: u.contentType,
            created: u.created,
            url: u.url,
          })));
        }
      }
    } catch {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);

    setErrors({});
  };

  const fetchVehicleDocumentTypes = async () => {
    setIsLoading(true);
    try {
      const results = await getFleetDocumentTypesAPI();

      setDocumentTypes(results);
    } catch {
      setDocumentTypes([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      FLEET_VEHICLE_DOCUMENT_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      let result;

      if (documentId) {
        result = await updateFleetDocumentAPI(
          vehicleId,
           "Vehicle",
          documentId,
          formValues,
          uploads,
          t
        );
      }
      else {
        result = await createFleetDocumentAPI(
          vehicleId,
          "Vehicle",
          formValues,
          uploads,
          t
        );
      }
      if (result && onSuccess) onSuccess();
    }
    catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleDropdownChange = useHandleDropdownChange(
    setFormValues,
    formValues
  );

  const handleUploadSave = useCallback(
    (newUploads: FleetDocumentUpload[]) => {
      setUploads((prev) => [...prev, ...newUploads]);
    },
    []
  );

  const handleUploadDelete = useCallback(
    (id: string) => {
      setUploads((prev) => prev.filter((u) => u.id !== id));
    },
    []
  );

  const documentTypeItems: DropdownOption[] = documentTypes.map((i) => {
    return {
      value: i.id,
      text: `${i.name}`,
    };
  });

  const handleCloseModal = () => {
    createDialog({
      onConfirm: onClose
    })
  }


  return (
    <>
      <div className="flex gap-4">
        <div className="w-full m-auto">
          <div className="flex gap-3 h-full">
            {/* pb-20: podnožje s gumbima je fixed na dnu modala, pa sadržaj ne smije ići ispod njega. */}
            <form noValidate className="text-left flex-1 p-4 pb-20">
              <div className="">
                <div className="flex flex-col gap-3 mb-3">
                  <div className="flex-1 ">
                    <Dropdown
                      name="documentTypeId"
                      label={t("admin.fleet.vehicle.document.add.documenttype")}
                      options={documentTypeItems}
                      onChange={handleDropdownChange}
                      value={formValues.documentTypeId}
                      error={errors.documentTypeId}
                      required
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <TextField
                    type="text"
                    name="name"
                    label={t("admin.fleet.vehicle.document.add.name")}
                    placeholder={t(
                      "admin.fleet.vehicle.document.add.name.placeholder"
                    )}
                    onChange={handleTextFieldChange}
                    error={errors.name}
                    value={formValues.name}
                    required
                    endIcon=""
                  ></TextField>
                </div>
                <div className="mb-3">
                  <TextArea
                    name="description"
                    label={t("admin.fleet.vehicle.document.add.description")}
                    placeholder={t(
                      "admin.fleet.vehicle.document.add.description.placeholder"
                    )}
                    onChange={handleTextFieldChange}
                    error={errors.description}
                    value={formValues.description}
                    required
                    endIcon=""
                  ></TextArea>
                </div>
                <div className="mb-3">
                  <FileUploadControl
                    uploads={uploads}
                    onUploadSave={handleUploadSave}
                    onUploadDelete={handleUploadDelete}
                    buttonText="Dodaj datoteke"
                    modalTitle="Upload dokumenta"
                    emptyText="Dodajte datoteke"
                  />
                </div>
              </div>
              <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                <Button
                  variant="outlined"
                  children={t("admin.close")}
                  onClick={handleCloseModal}
                />
                <Button
                  variant="primary"
                  children={t("admin.save")}
                  onClick={handleFormSubmit}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(VehicleDocumentEditCard);
